/**
 * API calls for the admin Report Quality tab.
 *
 * Auth is the app's cookie session, so every request carries `credentials: 'include'` —
 * there is no bearer token anywhere in this codebase.
 *
 * Every endpoint here is admin-only on the backend, deliberately: the classifier's own model card
 * says its score must not be shown to customers.
 */

const API_URL = import.meta.env.VITE_API_URL || '';

const BASE = '/admin/report-quality';

/** Verdict buckets, worst to best. */
export type Bucket = 'low' | 'mid' | 'high';

/** `ok` means scored; `error` means the run failed and `error` says why. */
export type ClassificationStatus = 'ok' | 'error';

export interface ClassificationSummary {
  status: ClassificationStatus;
  bucket: Bucket | null;
  score: number | null;
  confidence: number | null;
  /** P(low) — the classifier's intended ranking key for a review queue. */
  review_priority: number | null;
  classified_at: string | null;
  error: string | null;
}

/** A scored artifact: a simulation report or an hcv2 chat session. */
export type ItemKind = 'simulation' | 'chat';

export interface SimulationRow {
  /** `ratable_items`' namespace: "42" for a simulation, "chat_42" for a chat session. */
  item_ref: string;
  kind: ItemKind;
  id: number;
  name: string | null;
  use_case: string | null;
  created_at: string | null;
  is_ratable: boolean | null;
  /** Null means never classified — a different state from a failed run, which has status 'error'. */
  classification: ClassificationSummary | null;
}

export interface ClassificationDetail extends ClassificationSummary {
  id: number;
  use_case: string | null;
  reasoning: string | null;
  observations: { supporting?: string[]; detracting?: string[] } | null;
  anchors: Anchor[] | null;
  detail: Record<string, unknown> | null;
  warning: string | null;
  used_llm_judge: boolean | null;
  llm_judge_reasoning: string | null;
  model_version: string | null;
  /** What the classifier was actually shown, so an odd verdict can be traced back to its input. */
  raw_chars: number | null;
  sent_chars: number | null;
  personas_omitted: number | null;
  images_omitted: number | null;
}

/** A human-rated report the model found nearest to this one — the most useful part for a reviewer. */
export interface Anchor {
  rating: number;
  bucket: Bucket;
  similarity: number;
  rater_reasoning: string | null;
}

export interface SimulationListResponse {
  simulations: SimulationRow[];
  total: number;
  limit: number;
  offset: number;
}

export interface SimulationDetailResponse {
  simulation: SimulationRow;
  classifications: ClassificationDetail[];
}

export interface UseCaseOptions {
  use_cases: string[];
  /** Use cases the classifier reports itself as unreliable on. Named by the backend so there is
   *  one home for that advice rather than a copy in the UI. */
  unreliable: string[];
}

export type SortMode = 'recent' | 'priority';

/** The bucket filter's "never classified" option — matches the backend's pseudo-bucket. */
export const BUCKET_UNCLASSIFIED = 'unclassified';

export interface ListParams {
  search?: string;
  useCase?: string;
  bucket?: string;
  kind?: ItemKind;
  sort?: SortMode;
  limit?: number;
  offset?: number;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${API_URL}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    ...init,
  });

  if (response.status === 403) {
    throw new Error('Report quality is admin-only.');
  }
  if (!response.ok) {
    // Surface the backend's own message where it has one — it explains a failed re-classify
    // (broker down) far better than a status code does.
    let detail = '';
    try {
      detail = (await response.json())?.detail ?? '';
    } catch {
      /* non-JSON error body */
    }
    throw new Error(detail || `Request failed (${response.status})`);
  }
  return response.json();
}

export const fetchSimulations = (params: ListParams = {}) => {
  const qs = new URLSearchParams();
  if (params.search) qs.set('search', params.search);
  if (params.useCase) qs.set('use_case', params.useCase);
  if (params.bucket) qs.set('bucket', params.bucket);
  if (params.kind) qs.set('kind', params.kind);
  if (params.sort) qs.set('sort', params.sort);
  if (params.limit != null) qs.set('limit', String(params.limit));
  if (params.offset != null) qs.set('offset', String(params.offset));
  const suffix = qs.toString() ? `?${qs}` : '';
  return request<SimulationListResponse>(`${BASE}/simulations${suffix}`);
};

export const fetchClassifications = (itemRef: string) =>
  request<SimulationDetailResponse>(`${BASE}/simulations/${encodeURIComponent(itemRef)}`);

export const requestReclassify = (itemRef: string) =>
  request<{ queued: boolean; item_ref: string; task_id: string | null }>(
    `${BASE}/simulations/${encodeURIComponent(itemRef)}/classify`,
    { method: 'POST' },
  );

/**
 * A chat session's transcript, for the detail pane. Reuses the admin-guarded endpoint that
 * already serves this (misleadingly filed under free-tier, but not free-tier scoped) rather than
 * adding a second way to read the same rows.
 */
export const fetchChatMessages = (sessionId: number) =>
  request<{ id: number; title: string | null; messages: ChatMessage[] }>(
    `/admin/free-tier/chats/${sessionId}`,
  );

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  metadata?: { visual?: unknown } | null;
}

export const fetchUseCaseOptions = () => request<UseCaseOptions>(`${BASE}/use-cases`);
